import { IEvent } from '@app/api/interfaces/event.interface';
import { IPosition } from '@app/core/interfaces/position.interface';
import { DEFAULT_PRESSURE_UNIT } from '@app/shared/units/pressure/pressure-unit.localize';
import { Pressure } from '@app/shared/units/pressure/pressure.model';
import { DEFAULT_FLOW_UNIT } from '@app/shared/units/flow/flow-unit.localize';
import { Flow } from '@app/shared/units/flow/flow.model';
import { isSamePosition } from '@app/main/main-content/map/utils/geometry';
import { LdEventStatus } from '@app/main/leak-detection/enums/ld-event-status.enum';
import {
  deriveEventLeakSize,
  deriveEventRepairDate,
  deriveLdEventInvestigationResult,
  deriveLdEventRepairStatus,
  isEventLeakFound,
} from '@app/main/leak-detection/utils/ld-event-status';
import { IDialogEventDraft } from '@app/main/leak-detection/dialogs/dialog-event/interfaces/dialog-event-draft.interface';
import { DIALOG_EVENT_MEASURE_DECIMALS } from '@app/main/leak-detection/dialogs/dialog-event/configs/dialog-event-format.config';

export function toDraft(event: IEvent | null, position: IPosition | null): IDialogEventDraft {
  if (!event) {
    return {
      assignedTo: null,
      location: null,
      coordinates: position,
      status: LdEventStatus.New,
      investigationResult: null,
      repairStatus: null,
      repairDate: null,
      leakSize: null,
      leakSizeUnit: DEFAULT_FLOW_UNIT,
      pressure: null,
      pressureUnit: DEFAULT_PRESSURE_UNIT,
      soilComposition: null,
      nonLeakNoiseSource: null,
      nonLeakOtherDetails: null,
      hidden: false,
    };
  }

  const { investigationResult } = event;
  const noLeak = investigationResult && !isEventLeakFound(investigationResult) ? investigationResult : null;

  return {
    assignedTo: event.assignedTo ?? null,
    location: event.location ?? null,
    coordinates: event.coordinates,
    status: event.status,
    investigationResult: deriveLdEventInvestigationResult(event) ?? null,
    repairStatus: deriveLdEventRepairStatus(event) ?? null,
    repairDate: deriveEventRepairDate(event),
    leakSize: deriveEventLeakSize(event),
    leakSizeUnit: DEFAULT_FLOW_UNIT,
    pressure: investigationResult?.pressure ?? null,
    pressureUnit: DEFAULT_PRESSURE_UNIT,
    soilComposition: investigationResult?.soilComposition ?? null,
    nonLeakNoiseSource: noLeak?.nonLeakNoiseSource ?? null,
    nonLeakOtherDetails: noLeak?.nonLeakOtherDetails ?? null,
    hidden: event.hidden,
  };
}

export function isSameDraft(a: IDialogEventDraft, b: IDialogEventDraft): boolean {
  const keys = Object.keys(a) as (keyof IDialogEventDraft)[];

  return keys.every(key => {
    if (key === 'pressureUnit' || key === 'leakSizeUnit') return true;
    if (key === 'coordinates') return isSamePosition(a.coordinates, b.coordinates);

    return a[key] === b[key];
  });
}

export function pressureDisplayValue(draft: IDialogEventDraft): number | null {
  if (draft.pressure === null) return null;

  return new Pressure(draft.pressure, DEFAULT_PRESSURE_UNIT).round(draft.pressureUnit, DIALOG_EVENT_MEASURE_DECIMALS);
}

export function leakSizeDisplayValue(draft: IDialogEventDraft): number | null {
  if (draft.leakSize === null) return null;

  return new Flow(draft.leakSize, DEFAULT_FLOW_UNIT).round(draft.leakSizeUnit, DIALOG_EVENT_MEASURE_DECIMALS);
}

/** the statuses that cannot hold an unassigned event */
export function needsAssignee(status: LdEventStatus): boolean {
  return status === LdEventStatus.Investigation || status === LdEventStatus.Resolved;
}
